import React, { useState, useEffect, useCallback } from 'react';
import { suggestionsAPI, handleApiError } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { showSuccess, showError } from '../utils/toast';
import type { Suggestion, SuggestionStatus, SuggestionModalState, DeleteModalState, SuggestionFilter } from '../types';
import './Dashboard.css';
import './SuggestionsPage.css';

const SuggestionsPage: React.FC = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SuggestionFilter>('pending');

  // Modal states
  const [approveModal, setApproveModal] = useState<SuggestionModalState>({ isOpen: false, suggestion: null, notes: '' });
  const [rejectModal, setRejectModal] = useState<SuggestionModalState>({ isOpen: false, suggestion: null, notes: '' });
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({ isOpen: false, suggestion: null });
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const loadSuggestions = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (filter === 'pending') {
        response = await suggestionsAPI.getPendingSuggestions();
      } else {
        response = await suggestionsAPI.getAllSuggestions();
      }
      setSuggestions(response.data || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const handleApprove = async (): Promise<void> => {
    if (!approveModal.suggestion) return;
    setActionLoading(true);
    try {
      await suggestionsAPI.approveSuggestion(approveModal.suggestion.id, approveModal.notes);
      showSuccess('Предложението беше одобрено успешно');
      setApproveModal({ isOpen: false, suggestion: null, notes: '' });
      loadSuggestions();
    } catch (err) {
      showError('Грешка при одобряването: ' + handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    if (!rejectModal.suggestion) return;
    setActionLoading(true);
    try {
      await suggestionsAPI.rejectSuggestion(rejectModal.suggestion.id, rejectModal.notes);
      showSuccess('Предложението беше отхвърлено');
      setRejectModal({ isOpen: false, suggestion: null, notes: '' });
      loadSuggestions();
    } catch (err) {
      showError('Грешка при отхвърлянето: ' + handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteModal.suggestion) return;
    setActionLoading(true);
    try {
      await suggestionsAPI.deleteSuggestion(deleteModal.suggestion.id);
      showSuccess('Предложението беше изтрито успешно');
      setDeleteModal({ isOpen: false, suggestion: null });
      loadSuggestions();
    } catch (err) {
      showError('Грешка при изтриването: ' + handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeClass = (status: SuggestionStatus): string => {
    switch (status) {
      case 'pending': return 'badge-warning';
      case 'approved': return 'badge-success';
      case 'rejected': return 'badge-danger';
      case 'implemented': return 'badge-info';
      default: return 'badge-secondary';
    }
  };

  const getStatusText = (status: SuggestionStatus): string => {
    switch (status) {
      case 'pending': return 'Чака одобрение';
      case 'approved': return 'Одобрено';
      case 'rejected': return 'Отхвърлено';
      case 'implemented': return 'Реализирано';
      default: return status;
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Управление на предложения</h2>
          <p className="last-updated">Преглед и модерация на потребителски предложения</p>
          <div className="header-controls">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as SuggestionFilter)}
              className="filter-select"
            >
              <option value="pending">Чакащи одобрение</option>
              <option value="all">Всички предложения</option>
            </select>
          </div>
        </div>
        <button
          className={`floating-refresh ${loading ? 'loading' : ''}`}
          onClick={loadSuggestions}
          disabled={loading}
          title="Обновяване на предложенията"
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-lightbulb"></i>
            {filter === 'pending' ? 'Чакащи предложения' : 'Всички предложения'}
          </h3>
          <div className="suggestions-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Зареждане на предложения...</p>
          </div>
        </div>
      ) : suggestions.length === 0 ? (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-lightbulb"></i>
            {filter === 'pending' ? 'Чакащи предложения' : 'Всички предложения'}
          </h3>
          <div className="suggestions-empty">
            <i className="fas fa-lightbulb"></i>
            <p>{filter === 'pending' ? 'Няма чакащи предложения' : 'Няма предложения'}</p>
          </div>
        </div>
      ) : (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-lightbulb"></i>
            {filter === 'pending' ? 'Чакащи предложения' : 'Всички предложения'}
            <span className="section-count">({suggestions.length})</span>
          </h3>
          <div className="suggestions-list">
            {suggestions.map((suggestion) => (
              <div key={suggestion.id} className="suggestion-card">
                <div className="suggestion-header">
                  <div className="suggestion-meta">
                    <h3>{suggestion.title}</h3>
                    <div className="suggestion-details">
                      <span>от {suggestion.user.first_name} {suggestion.user.last_name}</span>
                      <span className="separator">•</span>
                      <span>{new Date(suggestion.created_at).toLocaleDateString('bg-BG')}</span>
                      <span className={`badge ${getStatusBadgeClass(suggestion.status)}`}>
                        {getStatusText(suggestion.status)}
                      </span>
                    </div>
                  </div>
                  <div className="suggestion-votes">
                    <span className="vote-count positive">
                      <i className="fas fa-thumbs-up"></i> {suggestion.votes_up}
                    </span>
                    <span className="vote-count negative">
                      <i className="fas fa-thumbs-down"></i> {suggestion.votes_down}
                    </span>
                  </div>
                </div>
                <div className="suggestion-description">
                  <p>{suggestion.description}</p>
                </div>
                {suggestion.admin_notes && (
                  <div className="admin-notes">
                    <strong>Административни бележки:</strong> {suggestion.admin_notes}
                  </div>
                )}
                <div className="suggestion-actions">
                  {suggestion.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={() => setApproveModal({ isOpen: true, suggestion, notes: '' })}
                      >
                        <i className="fas fa-check"></i> Одобри
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => setRejectModal({ isOpen: true, suggestion, notes: '' })}
                      >
                        <i className="fas fa-times"></i> Отхвърли
                      </button>
                    </>
                  )}
                  {suggestion.status === 'approved' && (
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => setDeleteModal({ isOpen: true, suggestion })}
                    >
                      <i className="fas fa-trash"></i> Изтрий
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approve Modal */}
      <ConfirmModal
        isOpen={approveModal.isOpen}
        onClose={() => setApproveModal({ isOpen: false, suggestion: null, notes: '' })}
        onConfirm={handleApprove}
        title="Одобряване на предложение"
        message={`Сигурни ли сте, че искате да одобрите предложението "${approveModal.suggestion?.title}"?`}
        confirmText="Одобри"
        variant="default"
        loading={actionLoading}
        showInput={true}
        inputLabel="Административни бележки (незадължително)"
        inputValue={approveModal.notes}
        onInputChange={(value) => setApproveModal(prev => ({ ...prev, notes: value }))}
        inputPlaceholder="Добавете бележки към одобрението..."
      />

      {/* Reject Modal */}
      <ConfirmModal
        isOpen={rejectModal.isOpen}
        onClose={() => setRejectModal({ isOpen: false, suggestion: null, notes: '' })}
        onConfirm={handleReject}
        title="Отхвърляне на предложение"
        message={`Сигурни ли сте, че искате да отхвърлите предложението "${rejectModal.suggestion?.title}"?`}
        confirmText="Отхвърли"
        variant="danger"
        loading={actionLoading}
        showInput={true}
        inputLabel="Причина за отхвърляне"
        inputValue={rejectModal.notes}
        onInputChange={(value) => setRejectModal(prev => ({ ...prev, notes: value }))}
        inputPlaceholder="Посочете причината за отхвърляне..."
      />

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, suggestion: null })}
        onConfirm={handleDelete}
        title="Изтриване на предложение"
        message={`Сигурни ли сте, че искате да изтриете предложението "${deleteModal.suggestion?.title}"? Това действие не може да бъде отменено.`}
        confirmText="Изтрий"
        variant="danger"
        loading={actionLoading}
      />
    </div>
  );
};

export default SuggestionsPage;
